// Help Button Component
// Floating help button that opens Help Center or Support Chat

import React, { useState } from 'react';
import { HelpCircle, X, Book, MessageCircle, Video, ChevronRight, Search, Bot } from 'lucide-react';
import { Link } from 'react-router-dom';

const HelpButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { role: 'assistant', content: "Hi! I'm your IntakePal assistant. How can I help you today?" }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const quickLinks = [
    { icon: Book, title: 'Help Center', description: 'Browse guides & FAQs', link: '/help' },
    { icon: Video, title: 'Video Tutorials', description: 'Watch how-to videos', link: '/help#videos' },
    { icon: MessageCircle, title: 'Chat with Support', description: 'Get instant answers', action: () => setShowChat(true) },
  ];

  const commonQuestions = [
    "How do I install the chat widget?",
    "What do lead scores mean?",
    "How do I export to my CMS?",
    "How do I customize branding?",
  ];

  const getAIResponse = (question) => {
    // Simple rule-based responses for common questions
    const responses = {
      'widget': `To install your chat widget:
1. Go to Settings → Embed Code
2. Copy the JavaScript snippet
3. Paste it before the </body> tag on your website
4. The widget will appear automatically!

Need the code? Go to /embed in your dashboard.`,
      'lead score': `Lead scores help prioritize your cases:
• **A (90-100):** Hot lead! High-value case, act fast
• **B (70-89):** Good potential, follow up soon  
• **C (50-69):** Average case, standard follow-up
• **D (30-49):** Lower priority
• **F (0-29):** May not be a fit

Focus your time on A and B leads first!`,
      'export': `To export leads to your CMS:
1. Go to the Leads page
2. Click "Export" on any lead (or bulk export)
3. Choose your CMS format (Clio, Filevine, etc.)
4. Download the CSV and import into your system

Pro tip: Set your preferred CMS in Settings to get auto-formatted exports!`,
      'brand': `To customize your branding:
1. Go to Setup Wizard → Branding step
2. Upload your firm's logo
3. Choose your primary color
4. Preview how the widget will look
5. Save your changes

Your branding will appear on the chat widget and client-facing pages.`,
      'default': `I can help you with:
• Installing the chat widget
• Understanding lead scores
• Exporting to your CMS
• Customizing branding
• Setting up notifications

What would you like to know more about?`
    };

    const q = question.toLowerCase();
    if (q.includes('widget') || q.includes('install') || q.includes('embed')) return responses.widget;
    if (q.includes('score') || q.includes('lead') || q.includes('grade')) return responses['lead score'];
    if (q.includes('export') || q.includes('cms') || q.includes('csv')) return responses.export;
    if (q.includes('brand') || q.includes('logo') || q.includes('color')) return responses.brand;
    return responses.default;
  };

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;

    const userMessage = inputValue.trim();
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setInputValue('');
    setIsTyping(true);

    // Simulate support response delay
    setTimeout(() => {
      const response = getAIResponse(userMessage);
      setChatMessages(prev => [...prev, { role: 'assistant', content: response }]);
      setIsTyping(false);
    }, 800);
  };

  const handleQuickQuestion = (question) => {
    setInputValue(question);
    setTimeout(() => handleSendMessage(), 100);
  };

  if (showChat) {
    return (
      <div className="fixed bottom-6 right-6 z-50" data-tour="help-button">
        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-96 max-h-[500px] flex flex-col overflow-hidden">
          {/* Chat Header */}
          <div className="bg-teal-500 text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold">IntakePal Assistant</h3>
                <p className="text-xs text-teal-100">Always here to help</p>
              </div>
            </div>
            <button 
              onClick={() => { setShowChat(false); setIsOpen(false); }}
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-72">
            {chatMessages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] p-3 rounded-2xl text-sm whitespace-pre-wrap ${
                  msg.role === 'user' 
                    ? 'bg-teal-500 text-white rounded-br-md' 
                    : 'bg-slate-100 text-slate-800 rounded-bl-md'
                }`}>
                  {msg.content}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-slate-100 p-3 rounded-2xl rounded-bl-md">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"></span>
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></span>
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Quick Questions */}
          {chatMessages.length <= 2 && (
            <div className="px-4 pb-2">
              <p className="text-xs text-slate-500 mb-2">Quick questions:</p>
              <div className="flex flex-wrap gap-1">
                {commonQuestions.slice(0, 2).map((q, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleQuickQuestion(q)}
                    className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded-full text-slate-600 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Chat Input */}
          <div className="p-3 border-t border-slate-200">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Ask me anything..."
                className="flex-1 px-4 py-2 border border-slate-200 rounded-full text-sm focus:outline-none focus:border-teal-500"
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputValue.trim()}
                className="px-4 py-2 bg-teal-500 text-white rounded-full text-sm font-medium hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50" data-tour="help-button">
      {isOpen && (
        <div className="absolute bottom-16 right-0 bg-white rounded-2xl shadow-2xl border border-slate-200 w-80 overflow-hidden mb-2 animate-in slide-in-from-bottom-2">
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white p-4">
            <h3 className="font-semibold text-lg">How can we help?</h3>
            <p className="text-sm text-teal-100">Find answers instantly</p>
          </div>

          {/* Search */}
          <div className="p-3 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search help articles..."
                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-500"
              />
            </div>
          </div>

          {/* Quick Links */}
          <div className="p-3">
            {quickLinks.map((item, idx) => (
              item.link ? (
                <Link
                  key={idx}
                  to={item.link}
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-3 p-3 hover:bg-slate-50 rounded-lg transition-colors"
                >
                  <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-teal-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm text-slate-900">{item.title}</p>
                    <p className="text-xs text-slate-500">{item.description}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </Link>
              ) : (
                <button
                  key={idx}
                  onClick={item.action}
                  className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 rounded-lg transition-colors text-left"
                >
                  <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-teal-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm text-slate-900">{item.title}</p>
                    <p className="text-xs text-slate-500">{item.description}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </button>
              )
            ))}
          </div>

          {/* Common Questions */}
          <div className="px-3 pb-3">
            <p className="text-xs text-slate-500 mb-2 px-3">Common questions</p>
            {commonQuestions.map((q, idx) => (
              <button
                key={idx}
                onClick={() => { setShowChat(true); setTimeout(() => handleQuickQuestion(q), 300); }}
                className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 ${
          isOpen 
            ? 'bg-slate-700 hover:bg-slate-800' 
            : 'bg-teal-500 hover:bg-teal-600 hover:scale-105'
        }`}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <HelpCircle className="w-6 h-6 text-white" />
        )}
      </button>
    </div>
  );
};

export default HelpButton;
